import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../lib/authFetch';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { 
  Card, CardHeader, CardTitle, CardContent, CardDescription,
  Button, Input, Modal, ModalFooter, Badge, Alert, AlertDescription
} from '../components/ui';
import { formatDate } from '../lib/utils';
import { 
  Plus, Trash2, RefreshCw, Globe, Lock, CheckCircle, Clock, XCircle, 
  Copy, Shield, Loader2, AlertCircle, Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SkeletonCustomDomains } from '../components/ui/Skeleton';

export default function CustomDomains() {
  const { user, profile, isPro, isAdmin } = useAuth();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDnsModal, setShowDnsModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [newDomain, setNewDomain] = useState('');
  const [defaultDomain, setDefaultDomain] = useState(null);
  const [addedDomainInfo, setAddedDomainInfo] = useState(null);

  const canAddCustomDomains = isPro || isAdmin || profile?.role === 'superadmin' || profile?.subscription_plan === 'pro';

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('custom_domains')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setDomains(data || []);

      const { data: settings } = await supabase
        .from('admin_settings')
        .select('value')
        .eq('key', 'default_landing_domain')
        .maybeSingle();
      if (settings?.value) {
        setDefaultDomain(typeof settings.value === 'string' ? JSON.parse(settings.value) : settings.value);
      }
    } catch (error) {
      toast.error('Failed to load domains');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddDomain = async (e) => {
    e.preventDefault();
    const cleanDomain = newDomain.toLowerCase().trim()
      .replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
    if (!cleanDomain) { toast.error('Please enter a domain'); return; }
    setSaving(true);
    const tid = toast.loading('Adding domain to Vercel...');
    try {
      const res = await authFetch('/api/custom-domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add', userId: user.id, domain: cleanDomain }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Domain added! Now configure your DNS.', { id: tid });
        setShowAddModal(false);
        setNewDomain('');
        setAddedDomainInfo({
          domain: cleanDomain,
          dnsRecords: data.dnsInstructions || data.domain?.verification_records || [],
          dnsTarget: data.dnsTarget,
          verified: data.verified,
        });
        setShowDnsModal(true);
        loadData();
      } else {
        toast.error(data.error || 'Failed to add domain', { id: tid });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to add domain', { id: tid });
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async (domain) => {
    setVerifying(domain.id);
    const tid = toast.loading('Checking DNS configuration with Vercel...');
    try {
      const res = await authFetch('/api/custom-domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify', domainId: domain.id }),
      });
      const data = await res.json();
      if (data.verified || data.status === 'verified') {
        toast.success('Domain verified! SSL certificate is active.', { id: tid });
      } else if (data.status === 'pending') {
        if (data.dnsRecords && data.dnsRecords.length > 0) {
          setAddedDomainInfo({ domain: domain.domain, dnsRecords: data.dnsRecords, dnsTarget: data.dnsTarget, verified: false });
          setShowDnsModal(true);
          toast.error('DNS not configured yet. See the required records below.', { id: tid });
        } else {
          toast.error('DNS not configured yet. Please add the CNAME record.', { id: tid });
        }
      } else {
        toast.error(data.message || 'Verification failed', { id: tid });
      }
      loadData();
    } catch (error) {
      toast.error('Verification check failed', { id: tid });
    } finally {
      setVerifying(null);
    }
  };

  const handleDeleteDomain = async (domain) => {
    if (!window.confirm(`Delete ${domain.domain}?\n\nThis will also remove it from Vercel.`)) return;
    const tid = toast.loading('Removing domain...');
    try {
      const res = await authFetch('/api/custom-domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', domainId: domain.id }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Domain removed', { id: tid });
        loadData();
      } else {
        toast.error(data.error || 'Failed to delete domain', { id: tid });
      }
    } catch (error) {
      toast.error('Failed to delete domain', { id: tid });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatusIcon = (status) => {
    if (status === 'verified') return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (status === 'failed')   return <XCircle className="h-4 w-4 text-red-500" />;
    return <Clock className="h-4 w-4 text-yellow-500" />;
  };

  const getStatusBadge = (status) => {
    const variants = { verified: 'success', failed: 'destructive', pending: 'warning' };
    return (
      <Badge variant={variants[status] || 'secondary'} className="gap-1">
        {getStatusIcon(status)}{status}
      </Badge>
    );
  };

  // DNS Records display — semantic tokens throughout
  const DnsRecordsDisplay = ({ domain, dnsRecords, dnsTarget }) => {
    const records = dnsRecords?.length > 0
      ? dnsRecords
      : [{ type: 'CNAME', name: domain, value: dnsTarget || 'cname.vercel-dns.com' }];
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Add the following DNS record(s) at your domain registrar:
        </p>
        {records.map((record, idx) => (
          <div key={idx} className="bg-muted/40 rounded-lg border border-border p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wide">Type</label>
                <p className="font-mono font-semibold text-lg">{record.type}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wide">Name / Host</label>
                <p className="font-mono font-semibold">
                  {record.name?.split('.')[0] || domain.split('.')[0] || '@'}
                </p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wide">Value / Target</label>
                <p className="font-mono text-sm break-all text-primary">{record.value}</p>
              </div>
            </div>
            <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => copyToClipboard(record.value)}>
              <Copy className="h-3 w-3 mr-2" />Copy Value
            </Button>
          </div>
        ))}
        <Alert className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            DNS changes can take <strong>5–30 minutes</strong> to propagate (sometimes up to 24 hours).
            After adding the record, click <strong>"Check DNS"</strong> to verify.
          </AlertDescription>
        </Alert>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Custom Domains</h1>
          <p className="text-muted-foreground">Manage domains for your landing pages</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {canAddCustomDomains && (
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4 mr-2" />Add Domain
            </Button>
          )}
        </div>
      </div>

      {/* Default Domain Info */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="p-2 bg-primary/10 rounded-lg h-fit">
              <Globe className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold mb-1">Default Landing Page Domain</h3>
              <p className="text-sm text-muted-foreground mb-3">
                All users can create landing pages using this domain:
              </p>
              <div className="flex items-center gap-2 bg-muted/40 rounded-lg p-3 border border-border">
                <code className="flex-1 text-sm font-mono text-foreground">
                  {defaultDomain?.domain || 'pages.revhapi.com'}
                </code>
                <Button size="sm" variant="ghost"
                  onClick={() => copyToClipboard(defaultDomain?.domain || 'pages.revhapi.com')}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Your pages are available at:{' '}
                <code className="bg-muted px-1 rounded text-foreground/80">
                  {defaultDomain?.domain || 'pages.revhapi.com'}/your-page-slug
                </code>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pro upsell for non-pro users */}
      {!canAddCustomDomains && (
        <Card className="border-dashed">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="p-2 bg-primary/10 rounded-lg h-fit">
                <Lock className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">Custom Domains — Pro Feature</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Upgrade to Pro to use your own domains for landing pages.
                </p>
                <ul className="text-sm space-y-1 mb-4">
                  {['Your own branding', 'Automatic SSL certificates', 'Better visitor trust', 'No RevHapi subdomain'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-primary" />{item}
                    </li>
                  ))}
                </ul>
                <Link to="/subscribe"><Button size="sm">Upgrade to Pro</Button></Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* How to set up (Pro users) */}
      {canAddCustomDomains && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="p-2 bg-muted rounded-lg h-fit">
                <Info className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-2">How to Set Up Your Domain</h3>
                <ol className="text-sm space-y-2 text-muted-foreground">
                  {[
                    'Click "Add Domain" and enter your domain (e.g., landing.yourdomain.com)',
                    <>Vercel will provide a <strong className="text-foreground">unique CNAME value</strong> for your domain</>,
                    'Add the DNS record at your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.)',
                    <>Click <strong className="text-foreground">"Check DNS"</strong> to verify — SSL is automatic once verified</>,
                  ].map((step, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="font-medium text-foreground flex-shrink-0">{i + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Domains list */}
      {canAddCustomDomains && (
        <Card>
          <CardHeader>
            <CardTitle>Your Custom Domains</CardTitle>
            <CardDescription>{domains.length} custom domain(s) configured</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <SkeletonCustomDomains />
            ) : domains.length === 0 ? (
              <div className="text-center py-8">
                <Globe className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground mb-4">No custom domains yet</p>
                <Button onClick={() => setShowAddModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />Add Your First Domain
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {domains.map((domain) => (
                  <div key={domain.id}
                    className="border border-border rounded-lg p-4 hover:bg-muted/40 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${
                          domain.status === 'verified' ? 'bg-green-500/10' : 'bg-yellow-500/10'
                        }`}>
                          <Globe className={`h-5 w-5 ${
                            domain.status === 'verified' ? 'text-green-500' : 'text-yellow-500'
                          }`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{domain.domain}</h3>
                            {getStatusBadge(domain.status)}
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            Added {formatDate(domain.created_at)}
                            {domain.verified_at && ` · Verified ${formatDate(domain.verified_at)}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button size="sm" variant="outline" disabled={verifying === domain.id}
                          onClick={() => handleVerifyDomain(domain)}>
                          {verifying === domain.id
                            ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            : <RefreshCw className="h-4 w-4 mr-1" />}
                          {domain.status === 'verified' ? 'Re-check' : 'Check DNS'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteDomain(domain)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>

                    {domain.status !== 'verified' && (
                      <div className="mt-4 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <p className="font-medium text-yellow-800 dark:text-yellow-300 mb-2">
                              DNS Configuration Required
                            </p>
                            <DnsRecordsDisplay
                              domain={domain.domain}
                              dnsRecords={domain.verification_records}
                              dnsTarget={domain.dns_target}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Domain Modal */}
      <Modal isOpen={showAddModal} onClose={() => { setShowAddModal(false); setNewDomain(''); }}
        title="Add Custom Domain" description="Enter your domain to get the DNS configuration">
        <form onSubmit={handleAddDomain}>
          <div className="space-y-4">
            <Input label="Domain Name" placeholder="landing.yourdomain.com" value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              hint="Enter a subdomain like app.yourdomain.com or pages.yourdomain.com" required />
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                After adding, you'll receive <strong>specific DNS records</strong> to configure at your domain registrar.
                Each domain gets a unique CNAME value from Vercel.
              </AlertDescription>
            </Alert>
          </div>
          <ModalFooter>
            <Button variant="outline" type="button" onClick={() => { setShowAddModal(false); setNewDomain(''); }}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>Add Domain</Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* DNS Config Modal */}
      <Modal isOpen={showDnsModal} onClose={() => { setShowDnsModal(false); setAddedDomainInfo(null); }}
        title="Configure DNS Records"
        description={`DNS configuration for ${addedDomainInfo?.domain}`} size="lg">
        {addedDomainInfo && (
          <div className="space-y-4">
            {addedDomainInfo.verified ? (
              <Alert className="border-green-500/30 bg-green-500/10">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <AlertDescription className="text-green-700 dark:text-green-300">
                  <strong>Domain already verified!</strong> Your domain is ready to use.
                </AlertDescription>
              </Alert>
            ) : (
              <DnsRecordsDisplay
                domain={addedDomainInfo.domain}
                dnsRecords={addedDomainInfo.dnsRecords}
                dnsTarget={addedDomainInfo.dnsTarget}
              />
            )}
          </div>
        )}
        <ModalFooter>
          <Button onClick={() => { setShowDnsModal(false); setAddedDomainInfo(null); }}>Got it</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
